import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import StadiumRoundedIcon from "@mui/icons-material/StadiumRounded";
import React, { useState } from "react";
import { CircleFlag } from "../../common/CircleFlag";
import { useBets } from "../../common/contexts/betContext";
import { useUser } from "../../common/contexts/userContext";
import { Bet } from "../../types/Bet";
import { Match } from "../../types/Match";
import { getMatchBettingState, getMatchKickoff } from "../../utils/betting";
import countryDict from "../../utils/countryDict";
import { matchVenueLabel } from "../../utils/venue";
import { ModalValue, initModalValue } from "./BetModalBody";
import { submitBet } from "./betSubmit";
import "./styles.scss";

interface BetSheetProps {
  bet?: Bet;
  match?: Match;
  /** Match used purely for display (names, flags, stage, kickoff). */
  displayMatch?: Match;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function flagCode(name: string | undefined): string {
  return (name && countryDict.get(name)) || "pl";
}

// Keep predictions to a single digit and allow clearing the field (null).
function sanitizeScore(raw: string): number | null {
  const digits = raw.replace(/[^0-9]/g, "").slice(0, 1);
  return digits === "" ? null : Number(digits);
}

function stageLabel(match?: Match): string {
  if (!match) return "";
  const { type, value } = match.matchType;
  return type === "Group" ? `Group ${value}` : value;
}

function kickoffLabel(match?: Match): string {
  const date = getMatchKickoff(match);
  if (!date) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const BetSheet: React.FC<BetSheetProps> = ({
  bet,
  match,
  displayMatch,
  setIsOpen,
}) => {
  const { user } = useUser();
  const { bets, setBets } = useBets();

  // The user's current prediction for this match — passed directly as `bet`, or
  // looked up by match id when the modal is opened from the fixtures list.
  const existingBet =
    bet ?? bets.find((b) => b.matchId === (match?.id ?? displayMatch?.id));

  // Seed the inputs with the saved prediction so re-opening shows the numbers
  // the user already bet. The sheet remounts on each open (MUI Modal unmounts
  // its children when closed), so this re-reads the latest saved bet every time.
  const [modalValue, setModalValue] = useState<ModalValue>(() =>
    existingBet
      ? {
          homeScore: existingBet.homeTeamScoreBet,
          awayScore: existingBet.awayTeamScoreBet,
        }
      : initModalValue
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const homeName = displayMatch?.homeTeam.internationalName || "";
  const awayName = displayMatch?.awayTeam.internationalName || "";
  const stage = stageLabel(displayMatch);
  const venue = displayMatch ? matchVenueLabel(displayMatch) : "";
  const kickoff = kickoffLabel(displayMatch);

  const bettingState = getMatchBettingState(displayMatch);
  const bettingOpen = bettingState === "open";
  const matchAlreadyPlayed = bettingState === "played";

  const isComplete =
    modalValue.homeScore !== null && modalValue.awayScore !== null;

  // Nothing to save when the inputs still match the already-saved prediction.
  const isUnchanged =
    existingBet !== undefined &&
    modalValue.homeScore === existingBet.homeTeamScoreBet &&
    modalValue.awayScore === existingBet.awayTeamScoreBet;

  const canSave = isComplete && !isUnchanged && bettingOpen && !isSubmitting;

  function close() {
    setModalValue(initModalValue);
    setIsOpen(false);
  }

  async function handleSave() {
    if (!canSave) return;
    setIsSubmitting(true);
    try {
      await submitBet({ modalValue, bet, match, userId: user.id, bets, setBets });
      close();
    } catch {
      alert("Something went wrong - try bet a match once again");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="betSheet">
      <div className="betSheet__handle" />

      <header className="betSheet__header">
        <div className="betSheet__meta">
          <StadiumRoundedIcon sx={{ fontSize: 16 }} />
          <span>
            {[stage, venue].filter(Boolean).join(" • ")}
          </span>
        </div>
        <h2 className="betSheet__title">Predict Score</h2>
        {kickoff && (
          <div className="betSheet__time">
            <ScheduleRoundedIcon sx={{ fontSize: 14 }} />
            <span>{kickoff}</span>
          </div>
        )}
      </header>

      <main className="betSheet__body">
        <div className="betSheet__team">
          <span className="betSheet__flag">
            <CircleFlag countryCode={flagCode(homeName)} />
          </span>
          <span className="betSheet__teamName">
            <span>{homeName}</span>
          </span>
          {bettingOpen ? (
            <input
              aria-label={`${homeName} predicted score`}
              className="betSheet__score no-spin"
              type="number"
              inputMode="numeric"
              min="0"
              max="99"
              value={modalValue.homeScore ?? ""}
              placeholder={(existingBet?.homeTeamScoreBet ?? 0).toString()}
              onChange={(e) =>
                setModalValue((prev) => ({
                  ...prev,
                  homeScore: sanitizeScore(e.target.value),
                }))
              }
            />
          ) : (
            <span className="betSheet__score betSheet__score--static">
              {existingBet ? existingBet.homeTeamScoreBet : "–"}
            </span>
          )}
        </div>

        <span className="betSheet__colon">:</span>

        <div className="betSheet__team">
          <span className="betSheet__flag">
            <CircleFlag countryCode={flagCode(awayName)} />
          </span>
          <span className="betSheet__teamName">
            <span>{awayName}</span>
          </span>
          {bettingOpen ? (
            <input
              aria-label={`${awayName} predicted score`}
              className="betSheet__score no-spin"
              type="number"
              inputMode="numeric"
              min="0"
              max="99"
              value={modalValue.awayScore ?? ""}
              placeholder={(existingBet?.awayTeamScoreBet ?? 0).toString()}
              onChange={(e) =>
                setModalValue((prev) => ({
                  ...prev,
                  awayScore: sanitizeScore(e.target.value),
                }))
              }
            />
          ) : (
            <span className="betSheet__score betSheet__score--static">
              {existingBet ? existingBet.awayTeamScoreBet : "–"}
            </span>
          )}
        </div>
      </main>

      {bettingOpen ? (
        <footer className="betSheet__footer">
          <button
            className="betSheet__save"
            disabled={!canSave}
            onClick={handleSave}
          >
            <CheckRoundedIcon sx={{ fontSize: 18 }} />
            {isSubmitting ? "Saving..." : "Save Bet"}
          </button>
          <button className="betSheet__cancel" onClick={close}>
            Cancel
          </button>
        </footer>
      ) : (
        <footer className="betSheet__footer">
          <div className="betSheet__closed">
            <LockRoundedIcon sx={{ fontSize: 16 }} />
            <span>
              {existingBet
                ? matchAlreadyPlayed
                  ? "Match already played."
                  : "Predictions are locked — this match has started."
                : matchAlreadyPlayed
                ? "You did not place a bet before kickoff. Match already played."
                : "You did not place a bet before kickoff."}
            </span>
          </div>
          <button className="betSheet__cancel" onClick={close}>
            Close
          </button>
        </footer>
      )}
    </div>
  );
};

export default BetSheet;
